import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import { useMe } from '../../../app/hooks/useMe'
import { useCreateImpersonationGrantMutation } from '../../../app/redux-fetch/apiImpersonation'
import messages from './messages'
import { CreateGrantApiError } from './types'

interface NewGrantModalProps {
  open: boolean
  onClose: () => void
}

const EXPIRES_IN_HOURS = [1, 2, 4, 8] as const
const DEFAULT_EXPIRES_HOURS = 4

// E11 R1 (WI 611) — create an impersonation grant. Grantee defaults to Self (the caller's active persona id)
// shown as a pill; Target is a required user id; Reason is a required textarea; ExpiresIn chips (1/2/4/8h,
// default 4h) compute expiresAt at submit. The grantor is the caller server-side. A 50204 ("exceeds the
// 8-hour maximum") 400 renders INLINE on the modal; every other error is surfaced by the shared toast.
//
// NOTE (WI 611 AC gap): the ACs describe async server-filtered grantee/target pickers, but this USERS slice
// exposes NO cross-tenant grantable-users endpoint (only tenant-scoped user lists). So the pickers are numeric
// user-id inputs; the server enforces the non-admin / active / not-deleted rules (sproc THROW 50202).
const NewGrantModal = ({ open, onClose }: NewGrantModalProps) => {
  const { formatMessage } = useIntl()
  const { enqueueSuccess } = useNotificationStack()
  const { me } = useMe()
  const selfId = me?.activePersona?.id

  const [createGrant, { isLoading }] = useCreateImpersonationGrantMutation()

  const [granteeId, setGranteeId] = useState<string>('')
  const [targetId, setTargetId] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [expiresHours, setExpiresHours] = useState<number>(DEFAULT_EXPIRES_HOURS)
  const [inlineError, setInlineError] = useState<string>('')

  // The effective grantee: the self persona unless the admin overrode it with an explicit id.
  const isSelfGrantee = granteeId.trim() === ''
  const effectiveGranteeId = isSelfGrantee ? selfId : Number(granteeId)

  const targetIdNum = Number(targetId)
  const canSubmit = useMemo(() => {
    return (
      effectiveGranteeId != null &&
      Number.isInteger(effectiveGranteeId) &&
      effectiveGranteeId > 0 &&
      Number.isInteger(targetIdNum) &&
      targetIdNum > 0 &&
      reason.trim().length > 0 &&
      !isLoading
    )
  }, [effectiveGranteeId, targetIdNum, reason, isLoading])

  const reset = () => {
    setGranteeId('')
    setTargetId('')
    setReason('')
    setExpiresHours(DEFAULT_EXPIRES_HOURS)
    setInlineError('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async () => {
    if (!canSubmit || effectiveGranteeId == null) return
    setInlineError('')
    // Compute expiresAt from now + the chosen window. toDate().toISOString() is always UTC (Z), so no dayjs
    // utc plugin dependency is needed.
    const expiresAt = dayjs().add(expiresHours, 'hour').toDate().toISOString()
    try {
      await createGrant({
        granteeUserId: effectiveGranteeId,
        targetUserId: targetIdNum,
        reason: reason.trim(),
        expiresAt,
      }).unwrap()
      enqueueSuccess(messages.createSuccess)
      handleClose()
    } catch (error) {
      const apiError = error as CreateGrantApiError
      if (apiError?.kind === 'exceeds-max-duration' || apiError?.kind === 'invalid-grant') {
        // Inline on the modal — the shared base query still toasts, but the inline message is the actionable
        // one (the 8-hour ceiling / invalid grantor-grantee-target). Modal stays open with values preserved.
        setInlineError(apiError.message)
        return
      }
      // unknown: the shared base-query toast already surfaced it; keep the modal open for a retry.
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{formatMessage(messages.newGrant)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {inlineError && <Alert severity='error'>{inlineError}</Alert>}

          <Box>
            <Typography variant='body2' sx={{ mb: 0.5, color: '#636363' }}>
              {formatMessage(messages.granteeLabel)}
            </Typography>
            <Stack direction='row' spacing={1} alignItems='center'>
              {isSelfGrantee && selfId != null && (
                <Chip color='primary' size='small' label={formatMessage(messages.granteeSelf)} />
              )}
              <TextField
                size='small'
                type='number'
                value={granteeId}
                onChange={(e) => setGranteeId(e.target.value)}
                placeholder={selfId != null ? String(selfId) : ''}
                inputProps={{ min: 1, 'aria-label': formatMessage(messages.granteeLabel) }}
              />
              {!isSelfGrantee && (
                <Button size='small' onClick={() => setGranteeId('')}>
                  {formatMessage(messages.granteeUseSelf)}
                </Button>
              )}
            </Stack>
          </Box>

          <TextField
            label={formatMessage(messages.targetLabel)}
            helperText={formatMessage(messages.targetHelp)}
            type='number'
            required
            fullWidth
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            inputProps={{ min: 1 }}
          />

          <TextField
            label={formatMessage(messages.reasonLabel)}
            required
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />

          <Box>
            <Typography variant='body2' sx={{ mb: 1, color: '#636363' }}>
              {formatMessage(messages.expiresInLabel)}
            </Typography>
            <Stack direction='row' spacing={1}>
              {EXPIRES_IN_HOURS.map((h) => (
                <Chip
                  key={h}
                  label={formatMessage(
                    h === 1
                      ? messages.expiresIn1h
                      : h === 2
                        ? messages.expiresIn2h
                        : h === 4
                          ? messages.expiresIn4h
                          : messages.expiresIn8h
                  )}
                  color={expiresHours === h ? 'primary' : 'default'}
                  variant={expiresHours === h ? 'filled' : 'outlined'}
                  onClick={() => setExpiresHours(h)}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button variant='contained' onClick={onSubmit} disabled={!canSubmit}>
          {formatMessage(messages.createSubmit)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewGrantModal
